import React, { useContext, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Menu,
    MenuItem,
    Checkbox,
    TableSortLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Toolbar,
    Typography,
    Box,
    CircularProgress,
    useMediaQuery,
    Chip,
} from '@mui/material';
import { Visibility, Delete as DeleteIcon, Edit as EditIcon, MoreVert as MoreVertIcon, Settings } from '@mui/icons-material';
import ReferralFormDialog from '../dialogs/ReferralFormDialog';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import moment from 'moment';
import { AuthContext } from '../../utils/AuthContext';

const columns = [
    { id: 'agent_name', label: 'Agent Name', minWidth: 120 },
    { id: 'client_name', label: 'Client Name', minWidth: 120 },
    { id: 'birthday', label: 'Birthday', minWidth: 100 },
    { id: 'nationality', label: 'Nationality', minWidth: 110 },
    { id: 'residence', label: 'Residence', minWidth: 110 },
    { id: 'email', label: 'Email', minWidth: 120 },
    { id: 'contact_no', label: 'Contact No', minWidth: 120 },
    { id: 'remarks', label: 'Remarks', minWidth: 120 },
    { id: 'status', label: 'Status', minWidth: 120 },
    { id: 'created_at', label: 'Date Added', minWidth: 120 },
    { id: 'actions', label: 'Actions', width: 100, align: 'center', sortable: false },
];

export default function ReferralsTable({
    referrals,
    page,
    rowsPerPage,
    totalRecords,
    onPageChange,
    onRowsPerPageChange,
    onDelete,
    isLoading,
    handleFetchReferrals,
    setReferralToDelete,
}) {
    const isMobile = useMediaQuery('(max-width:600px)');
    const [visibleColumns, setVisibleColumns] = useState(columns.map((col) => col.id));
    const [openColumnDialog, setOpenColumnDialog] = useState(false);
    const [selected, setSelected] = useState([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);

    const [selectedReferral, setSelectedReferral] = useState(null);
    const [openReferralFormDialog, setOpenReferralFormDialog] = useState('')

    const [mode, setMode] = useState('view');

    const { auth } = useContext(AuthContext);

    const toggleColumnVisibility = (columnId) => {
        setVisibleColumns((prev) =>
            prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
        );
    };

    const handleOpenColumnDialog = () => {
        setOpenColumnDialog(true);
    };

    const handleCloseColumnDialog = () => {
        setOpenColumnDialog(false);
    };

    const isSelected = (id) => selected.includes(id);

    const handleMenuOpen = (event, rowId) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRowId(null);
    };

    const renderCellContent = (column, row) => {
        if (column.id === 'actions') {
            return (
                <>
                    <IconButton onClick={(event) => handleMenuOpen(event, row.id)}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={menuRowId === row.id}
                        onClose={handleMenuClose}
                    >
                        <MenuItem
                            onClick={() => {
                                setSelectedReferral(row);
                                setOpenReferralFormDialog(true);
                                setMode('view');
                                handleMenuClose();
                            }}
                        >
                            <Visibility fontSize="small" sx={{ mr: 1 }} /> View
                        </MenuItem>

                        {
                            (row['status'] === 'New' || auth.user.user_type === 'Admin') && <MenuItem
                                onClick={() => {
                                    setSelectedReferral(row);
                                    setOpenReferralFormDialog(true);
                                    setMode('update');
                                    handleMenuClose();
                                }}
                            >
                                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                            </MenuItem>
                        }

                        {
                            (row['status'] === 'New') && <MenuItem
                                onClick={() => {
                                    setReferralToDelete(row);
                                    handleMenuClose();
                                }}
                            >
                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                            </MenuItem>
                        }

                    </Menu>
                </>
            );
        } else if (column.id === 'birthday' || column.id === 'created_at') {
            return moment(row[column.id]).format('MM/DD/YYYY');
        } else if (column.id === 'status') {
            const status = row[column.id];

            return <Chip
                sx={{
                    backgroundColor: getStatusBackgroundColor(status),
                    color: getStatusColor(status)
                }}
                label={status} />;
        } else {
            return row[column.id];
        }
    };

    const mappedReferrals = referrals.map((referral, index) => ({
        id: referral.id || index + 1,
        agent_name: referral.user.full_name,
        client_name: referral.client_name,
        birthday: referral.date_of_birth,
        nationality: referral.nationality,
        residence: referral.residence,
        email: referral.user.email,
        contact_no: referral.contact_no,
        remarks: referral.remarks,
        status: referral.status,
        created_at: referral.created_at,
    }));

    const sortedReferrals = [...mappedReferrals].sort((a, b) => {
        if (orderBy) {
            const isAsc = order === 'asc';
            if (typeof a[orderBy] === 'string') {
                return isAsc
                    ? a[orderBy].localeCompare(b[orderBy])
                    : b[orderBy].localeCompare(a[orderBy]);
            }
            return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
        }
        return 0;
    });


    const handleDeleteDialogOpen = () => {
        setOpenDeleteDialog(true);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function getStatusBackgroundColor(status) {
        if (status === 'New') {
            return '#DFF3FF';
        } else if (status === 'Pending') {
            return '#FEF2E5';
        } else if (status === 'Cancelled') {
            return '#FBE7E8';
        } else if (status === 'Closed') {
            return '#EBF9F1';
        }

        return null;
    }

    function getStatusColor(status) {
        if (status === 'New') {
            return '#2F69C1';
        } else if (status === 'Pending') {
            return '#CD6200';
        } else if (status === 'Cancelled') {
            return '#A30D11';
        } else if (status === 'Closed') {
            return '#1F9254';
        }

        return null;
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>
            {/* Toolbar */}
            {/* <Toolbar>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteDialogOpen}
                        sx={{
                            visibility: selected.length > 0 ? 'visible' : 'hidden', // Toggle visibility based on selected.length
                            mr: 2, // Space between button and table
                        }}
                    >
                        Delete {selected.length} {selected.length > 1 ? 'Items' : 'Item'}
                    </Button>
                    <Button
                        startIcon={<Settings />}
                        onClick={handleOpenColumnDialog}
                        variant="contained"
                    >
                        Column Settings
                    </Button>
                </Box>
            </Toolbar> */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'end' }}>
                <Button
                    startIcon={<ViewWeekOutlinedIcon />}
                    onClick={handleOpenColumnDialog}
                    variant="text"
                >
                    Column Settings
                </Button>
            </Box>

            {/* Table */}
            <TableContainer variant='outlined' component={Paper} sx={{ flexGrow: 1, overflowY: 'auto', width: '99.50%' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {/* <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected.length > 0 && selected.length === sortedReferrals.length}
                                        onChange={(event) =>
                                            setSelected(event.target.checked ? sortedReferrals.map((row) => row.id) : [])
                                        }
                                    />
                                </TableCell> */}
                                {columns
                                    .filter((col) => visibleColumns.includes(col.id))
                                    .map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{ minWidth: column.minWidth, fontWeight: 600, color: '#424242' }}
                                        >
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(column.id)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                sortedReferrals.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={visibleColumns.length + 1} sx={{ textAlign: 'center', height: 40 }}>No Referrals</TableCell>
                                </TableRow>

                            }
                            {sortedReferrals.map((row) => (
                                <TableRow key={row.id}>
                                    {/* <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isSelected(row.id)}
                                            onChange={() =>
                                                setSelected((prev) =>
                                                    isSelected(row.id)
                                                        ? prev.filter((id) => id !== row.id)
                                                        : [...prev, row.id]
                                                )
                                            }
                                        />
                                    </TableCell> */}
                                    {columns
                                        .filter((col) => visibleColumns.includes(col.id))
                                        .map((column) => (
                                            <TableCell key={column.id} align={column.align}>
                                                {renderCellContent(column, row)}
                                            </TableCell>
                                        ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                {
                    (selectedReferral && openReferralFormDialog) && (
                        <ReferralFormDialog referralParam={selectedReferral} dialogOpen={openReferralFormDialog} setDialogOpen={setOpenReferralFormDialog} callback={handleFetchReferrals} mode={mode} />
                    )
                }
            </TableContainer>
            <TablePagination
                sx={{ overflowY: 'hidden', minHeight: '45px', mb: 4 }}
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />

            {/* Column Visibility Dialog */}
            <Dialog open={openColumnDialog} onClose={handleCloseColumnDialog}>
                <DialogTitle>Column Settings</DialogTitle>
                <DialogContent>
                    {columns.map((column) => (
                        <FormControlLabel
                            key={column.id}
                            control={
                                <Checkbox
                                    checked={visibleColumns.includes(column.id)}
                                    onChange={() => toggleColumnVisibility(column.id)}
                                />
                            }
                            label={column.label}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseColumnDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}